/**
 * Auth Service - Handles session-based authentication API calls
 *
 * NOTE: Uses raw fetch() because these session endpoints are not yet in api-types.d.ts
 * Once the backend API types are regenerated (run: npx openapi-typescript ...),
 * this can be updated to use apiClient for type safety.
 *
 * Backend requirement: POST /auth/google/auth-with-session needs to be created
 * to support Google OAuth with session management.
 */

import { getAccessToken } from './tokenManager';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002';

// Response types for session-based auth endpoints
export interface SessionAuthResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token?: string;
  user?: any; // User data (included when using fallback Google auth)
}

export interface AuthError {
  status: number;
  message?: string;
  error?: string;
}

/**
 * Login with email and password using session-based authentication
 * Returns refresh token in response body (mobile client flow)
 *
 * @param email - User email
 * @param password - User password
 * @returns Session auth response with access token and refresh token
 * @throws AuthError on failure
 */
export const loginWithSession = async (
  email: string,
  password: string,
  recaptchaToken: string
): Promise<SessionAuthResponse> => {
  const response = await fetch(`${API_URL}/auth/login-with-session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Client-Type': 'mobile',
      'x-recaptcha-token': recaptchaToken,
    },
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error: AuthError = {
      status: response.status,
      message: errorData.message || 'Login failed',
      error: errorData.error,
    };
    throw error;
  }

  return response.json();
};

/**
 * Google OAuth login with session-based authentication
 * Exchanges Google auth code for session tokens
 *
 * Returns new session tokens with short-lived access token (15m) and refresh token.
 * Mobile clients receive refresh_token in response body via X-Client-Type header.
 *
 * @param code - Google OAuth authorization code
 * @returns Session auth response with access token, refresh token, and user data
 * @throws AuthError on failure
 */
export const googleAuthWithSession = async (
  code: string
): Promise<SessionAuthResponse> => {
  const response = await fetch(`${API_URL}/auth/google/auth`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Client-Type': 'mobile',
    },
    body: JSON.stringify({ code }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error: AuthError = {
      status: response.status,
      message: errorData.message || 'Google authentication failed',
      error: errorData.error,
    };
    throw error;
  }

  const data = await response.json();

  // Fetch user data using the access token
  const userResponse = await fetch(`${API_URL}/auth/user`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${data.access_token}`,
    },
  });

  if (!userResponse.ok) {
    const error: AuthError = {
      status: userResponse.status,
      message: 'Failed to fetch user data after Google authentication',
    };
    throw error;
  }

  const userData = await userResponse.json();

  // Return combined response with user data
  return {
    access_token: data.access_token,
    token_type: data.token_type || 'Bearer',
    expires_in: data.expires_in || 900,
    refresh_token: data.refresh_token,
    user: userData,
  };
};

/**
 * Logout from current session
 * Revokes the session on the backend
 *
 * @throws AuthError on failure (but logout should still clear local state)
 */
export const logout = async (): Promise<void> => {
  const token = getAccessToken();

  const response = await fetch(`${API_URL}/auth/logout`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Client-Type': 'mobile',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });

  if (!response.ok) {
    // Log but don't throw - we still want to clear local state
    console.warn('Logout API call failed:', response.status);
  }
};

/**
 * Logout from all sessions
 * Revokes all user sessions across all devices
 *
 * @param includeCurrentSession - Whether to also logout current session
 * @throws AuthError on failure
 */
export const logoutAll = async (
  includeCurrentSession = true
): Promise<void> => {
  const token = getAccessToken();

  const response = await fetch(`${API_URL}/auth/logout-all`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Client-Type': 'mobile',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    body: JSON.stringify({ include_current_session: includeCurrentSession }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error: AuthError = {
      status: response.status,
      message: errorData.message || 'Logout all failed',
      error: errorData.error,
    };
    throw error;
  }
};
